/**
 * Google Maps Places API を使用した住所自動補完入力フィールド
 * FL-35: リアルタイム地図表示機能の実装（Google Maps API統合）
 *
 * 責務:
 * - Google Maps Places Autocomplete API との連携
 * - リアルタイム住所補完候補の提示
 * - ユーザー選択時の距離計算トリガー
 * - 入力値の変更検知と親コンポーネントへの通知
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { googleMapsService } from '@/services/maps/googleMapsService';
import type { PlaceAutocompleteResult } from '@/types/maps';

export interface AddressAutocompleteInputProps {
  /** 入力フィールドのid属性（アクセシビリティ） */
  id?: string;

  /** ラベルテキスト */
  label?: string;

  /** プレースホルダーテキスト */
  placeholder?: string;

  /** 現在の入力値 */
  value: string;

  /** 入力値変更時のコールバック */
  onChange: (value: string) => void;

  /** 住所選択時のコールバック（距離計算トリガー）
   * @param address 選択された住所テキスト
   * @param placeId Google Maps Place ID（将来の拡張用）
   */
  onAddressSelect: (address: string, placeId?: string) => void;

  /** 必須フィールドか */
  required?: boolean;

  /** エラーメッセージ */
  error?: string;

  /** 無効化状態 */
  disabled?: boolean;

  /** クラス名（Tailwind対応） */
  className?: string;
}

/**
 * Google Maps Places Autocomplete APIを使用した住所入力コンポーネント
 *
 * 機能:
 * - 3文字以上入力時にリアルタイム住所補完を提示
 * - ドロップダウンからの選択で onAddressSelect を発火
 * - 入力値の即座の通知で formData を更新
 * - エラーハンドリングとロード状態表示
 *
 * @example
 * <AddressAutocompleteInput
 *   label="引取先住所"
 *   placeholder="〇〇県〇〇市..."
 *   value={formData.pickup_address_custom}
 *   onChange={(addr) => setFormData({...formData, pickup_address_custom: addr})}
 *   onAddressSelect={(addr) => calculateDistance(addr, deliveryAddr)}
 *   required
 * />
 */
export const AddressAutocompleteInput = React.forwardRef<
  HTMLInputElement,
  AddressAutocompleteInputProps
>(
  (
    {
      id,
      label,
      placeholder = '住所を入力してください',
      value,
      onChange,
      onAddressSelect,
      required = false,
      error,
      disabled = false,
      className = '',
    },
    ref
  ) => {
    // ドロップダウン表示制御
    const [isOpen, setIsOpen] = useState(false);

    // 補完候補リスト
    const [suggestions, setSuggestions] = useState<PlaceAutocompleteResult[]>([]);

    // ロード状態
    const [isLoading, setIsLoading] = useState(false);

    // エラー状態
    const [suggestionsError, setSuggestionsError] = useState<string>();

    // 選択されたサジェストインデックス（キーボード操作用）
    const [selectedIndex, setSelectedIndex] = useState(-1);

    // ドロップダウンコンテナへの参照（click outside検知用）
    const containerRef = useRef<HTMLDivElement>(null);

    // デバッグ：disabled 状態の監視
    useEffect(() => {
      console.log('[AddressAutocompleteInput] disabled 状態変更:', {
        disabled,
        label,
        id,
        value,
      });
    }, [disabled, label, id, value]);

    /**
     * Google Maps Places Autocomplete API を呼び出し、補完候補を取得
     */
    const fetchSuggestions = useCallback(async (input: string) => {
      // 入力が3文字未満の場合はスキップ
      if (input.trim().length < 3) {
        setSuggestions([]);
        setSelectedIndex(-1);
        return;
      }

      setIsLoading(true);
      setSuggestionsError(undefined);

      try {
        const results = await googleMapsService.getPlaceAutocomplete(input);
        setSuggestions(results);
        setSelectedIndex(-1); // リセット
      } catch (err) {
        console.error('住所補完取得エラー:', err);
        setSuggestionsError(err instanceof Error ? err.message : '住所補完の取得に失敗しました');
      } finally {
        setIsLoading(false);
      }
    }, []);

    /**
     * 入力値変更時のハンドラー
     */
    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.value;

        // デバッグ：入力イベント発火確認
        console.log('[AddressAutocompleteInput] 入力変更検出:', {
          newValue,
          length: newValue.length,
          disabled,
          isLoading: isLoading,
          isOpen,
        });

        // 親コンポーネントに値を通知（formData更新）
        onChange(newValue);

        // ドロップダウンを開く（3文字以上）
        setIsOpen(newValue.trim().length >= 3);

        // 補完候補を取得
        fetchSuggestions(newValue);
      },
      [onChange, fetchSuggestions, disabled, isLoading, isOpen]
    );

    /**
     * サジェスト選択時のハンドラー
     */
    const handleSuggestionSelect = useCallback(
      (suggestion: PlaceAutocompleteResult) => {
        // 入力フィールドに住所テキストを設定
        const selectedAddress = suggestion.description;
        onChange(selectedAddress);

        // 親コンポーネントに選択を通知（距離計算トリガー）
        onAddressSelect(selectedAddress, suggestion.place_id);

        // ドロップダウンを閉じる
        setIsOpen(false);
        setSuggestions([]);
        setSelectedIndex(-1);
      },
      [onChange, onAddressSelect]
    );

    /**
     * キーボード操作（矢印キー、Enter）ハンドラー
     */
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || suggestions.length === 0) {
          return;
        }

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
            break;

          case 'ArrowUp':
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
            break;

          case 'Enter':
            e.preventDefault();
            if (selectedIndex >= 0) {
              handleSuggestionSelect(suggestions[selectedIndex]);
            }
            break;

          case 'Escape':
            e.preventDefault();
            setIsOpen(false);
            break;

          default:
            break;
        }
      },
      [isOpen, suggestions, selectedIndex, handleSuggestionSelect]
    );

    /**
     * 外側クリック検知（ドロップダウン閉じる）
     */
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
      return undefined;
    }, [isOpen]);

    return (
      <div ref={containerRef} className={`relative w-full ${className}`}>
        {/* ラベル */}
        {label && (
          <label
            htmlFor={id}
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            {label}
            {required && <span className='text-red-500'> *</span>}
          </label>
        )}

        {/* 入力フィールド */}
        <div className='relative'>
          <input
            ref={ref}
            id={id}
            type='text'
            placeholder={placeholder}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (value.trim().length >= 3 && suggestions.length > 0) {
                setIsOpen(true);
              }
            }}
            disabled={disabled}
            required={required}
            className={`w-full rounded-md border px-3 py-2 text-sm shadow-sm transition-colors ${
              error
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-700 focus:border-blue-500 focus:ring-blue-500'
            } ${disabled ? 'cursor-not-allowed bg-gray-100 dark:bg-gray-700' : 'bg-white dark:bg-gray-800'} focus:outline-none focus:ring-1 text-gray-900 dark:text-white`}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
          />

          {/* ロード中インジケーター */}
          {isLoading && (
            <div className='absolute right-3 top-1/2 -translate-y-1/2'>
              <div className='h-4 w-4 animate-spin rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-blue-500'></div>
            </div>
          )}
        </div>

        {/* エラーメッセージ */}
        {error && (
          <p id={`${id}-error`} className='mt-1 text-sm text-red-500'>
            {error}
          </p>
        )}

        {/* API取得エラー */}
        {suggestionsError && (
          <p className='mt-1 text-sm text-orange-500'>補完提案の取得に失敗しました</p>
        )}

        {/* ドロップダウン */}
        {isOpen && suggestions.length > 0 && (
          <ul
            className='absolute top-full z-10 mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg'
            role='listbox'
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.place_id}
                role='option'
                aria-selected={index === selectedIndex}
                className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                  index === selectedIndex
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                }`}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {/* 主要テキスト */}
                <div className='font-medium'>{suggestion.main_text}</div>

                {/* 副要素テキスト（存在する場合） */}
                {suggestion.secondary_text && (
                  <div className='text-xs text-gray-600 dark:text-gray-400'>
                    {suggestion.secondary_text}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* 補完候補なし */}
        {isOpen && suggestions.length === 0 && !isLoading && value.trim().length >= 3 && (
          <div className='absolute top-full z-10 mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 shadow-lg'>
            該当する住所が見つかりません
          </div>
        )}
      </div>
    );
  }
);

AddressAutocompleteInput.displayName = 'AddressAutocompleteInput';
